// The treatment presentation wire contract. TreatmentPresentations::Composer (Ruby) writes the deck with snake_case
// keys and Inertia's prop transformer camelizes every hash KEY on the way out, never a value: slide types, family
// keys, path keys and public ids arrive exactly as Ruby wrote them. One field is deliberately a JSON string: a
// picture's Pearl marks (pearlJson), because PearlShapes reads Pearl's own snake_case keys.

import type { LocationId, PatientId, PublicId } from "~/api";
import type { LibraryPageId } from "~/practice_library/types";
import type { TreatmentVisitId } from "~/api/treatment_visits";
import type { TreatmentId } from "~/api/treatments";
import type { DeckLabelKey } from "./deck_words";
import type { Stroke } from "./markup";
import type { MarkupLabels } from "./markup_layer";

export type TreatmentPresentationId = PublicId<"tpres">;
export type TreatmentPresentationSnapshotId = PublicId<"tpsnap">;
export type PhotoId = PublicId<"ptclph">;
export type AccountUserId = PublicId<"actusr">;
export type PatientFileId = PublicId<"ptfile">;

export const FAMILY_KEYS = [
  "crown",
  "implant",
  "filling",
  "bridge",
  "orthodontics",
  "dentures",
  "extraction",
  "root_canal",
  "deep_cleaning",
  "bone_graft",
  "sedation",
  "night_guard",
  "gum_surgery",
  "veneer",
  "inlay_onlay",
  "whitening",
] as const;
export type FamilyKey = (typeof FAMILY_KEYS)[number];

/** One drawing per family; the art keys are the family keys. */
export type ArtKey = FamilyKey;

export const WAIT_PATHS = [
  "crack",
  "decay_to_nerve",
  "nerve_infection",
  "gum_disease",
  "worn_fillings",
  "missing_tooth",
  "after_root_canal",
] as const;
export type WaitPath = (typeof WAIT_PATHS)[number];

/** Universal tooth numbers 1-32; a primary tooth is drawn at its permanent position. */
/** How close Your teeth draws the mouth (SlideEdits::TEETH_ZOOMS). */
export type ArchZoom = "both" | "upper" | "lower" | "planned";

/** A part of the arch drawing, in its own units (ARCH_WIDTH by ARCH_HEIGHT). */
export type ArchFrame = { x: number; y: number; width: number; height: number };

/** The mouth today, or once the plan is done (SlideEdits::TEETH_MOMENTS). */
export type ArchMoment = "now" | "after";

/** Your teeth's drawing as staff or Ask Twelve set it, every part optional. A frame wins over around, and around over
 *  the zoom. */
export type TeethDrawing = {
  /** The part staff dragged. */
  frame?: ArchFrame;
  /** A close view of these teeth. */
  around?: number[];
  /** The teeth that stand out; the rest fade. */
  focus?: number[];
  /** False hides the tooth numbers. */
  numbers?: boolean;
  moment?: ArchMoment;
};

/** The mouth once the plan is done: planned teeth restored, a tooth coming out gone unless the plan replaces it. */
export type ArchAfter = { restored: number[]; missing: number[] };

export type Arch = {
  lit: number[];
  tinted: number[];
  missing: number[];
  labels: { tooth: number; text: string }[];
};

export type DeckPicture = {
  photoId: PhotoId;
  kind: "xray" | "photo";
  /** The mount series and slot, "Bitewings · UR BW". */
  label: string;
  /** ISO date the picture was taken. */
  takenOn: string;
  /** Oriented pixel size: a layout hint only. Marks align to the loaded image. */
  width: number | null;
  height: number | null;
  /** The pixels the marks were composed against ("3-90-10"). The staff page puts it in the image URL, so a picture
   *  rotated since the draft fails to load instead of showing marks on the wrong teeth. */
  orientationKey: string;
  /** JSON of {width, height, annotations}: Pearl's own result, patient categories only. Null draws no marks. */
  pearlJson: string | null;
};

type SlideBase = {
  /** Unique within the deck: the slide type, "family_<key>", "picture_<photo id>", "library_<page id>", "text_<n>" or
   *  "images_<n>". */
  key: string;
  eyebrow: string;
  headline: string;
  /** Staff hid it: the Build tab still lists it, faded. Never set on a shared copy, which leaves hidden slides out. */
  hidden?: boolean;
};

export type CoverSlide = SlideBase & {
  type: "cover";
  /** "3 teeth", "1 area", "2 visits", "Presented by Dr. Rivera". */
  chips: string[];
  arch: Arch;
};

export type WhatWeFoundSlide = SlideBase & {
  type: "what_we_found";
  /** "What we want Pat to know". */
  boxTitle: string;
  /** The box: 0 to 3 sentences. */
  box: string[];
  /** Empty since each picture got a slide of its own; a copy frozen before then still shows them beside the words. */
  pictures: DeckPicture[];
};

/** One picture, as large as the slide allows. Its headline names the kind: "X-ray" or "Photo". */
/** A pointer staff put on a picture: where, as shares of the oriented picture, and its few words. */
export type PictureNote = { x: number; y: number; text: string };

export type PictureSlide = SlideBase & {
  type: "picture";
  picture: DeckPicture;
  /** The part staff or Ask Twelve framed, as shares of the picture; absent for all of it. */
  frame?: PageFrame;
  /** False hides Pearl's marks. */
  marks?: boolean;
  notes?: PictureNote[];
  /** Staff's words under the picture. */
  words?: string;
};

/** A picture from the patient's Files, as staff's own picture slides carry it. */
export type DeckFilePicture = {
  fileId: PatientFileId;
  /** The file's description, else its name. */
  label: string;
  /** Pixel size of the stored file: a layout hint only. */
  width?: number | null;
  height?: number | null;
};

/** One picture on staff's own picture slide: from Imaging (drawn as taken, without Pearl's marks) or from Files, with
 *  staff's caption under it. */
export type ImagesItem = (
  | { picture: DeckPicture; file?: undefined }
  | { file: DeckFilePicture; picture?: undefined }
) & { caption?: string };

/** Staff's own pictures: one filling the slide, or two side by side, and their words under them. */
export type ImagesSlide = SlideBase & {
  type: "images";
  items: ImagesItem[];
  words?: string;
};

/** A page from the practice's own library, as the Composer writes it on a slide: the image; the slide's headline is
 *  its staff title. */
export type DeckLibraryPage = {
  pageId: LibraryPageId;
  /** Pixel size: a layout hint only. */
  width: number | null;
  height: number | null;
  /** A video page: the image is its still, and the slide plays the video from its own route. */
  video?: true;
};

/** A part of a library page, as shares of its width and height. */
export type PageFrame = { x: number; y: number; width: number; height: number };

/** One of the practice's own pages, after the treatment it is an example of. */
export type LibraryPageSlide = SlideBase & {
  type: "library_page";
  page: DeckLibraryPage;
  /** The part of the page staff or Ask Twelve framed; absent for the whole page. */
  frame?: PageFrame;
  /** The office's words beside the page, tying it to this patient's plan. */
  words?: string;
  /** The treatments it shows, by title: "Crown". */
  treatments: string[];
  /** "An example from Prosthodent". */
  caption: string;
};

export type YourTeethSlide = SlideBase & {
  type: "your_teeth";
  /** Absent on a copy frozen before staff could zoom: the whole mouth. */
  zoom?: ArchZoom;
  /** Absent on a copy frozen before staff could frame, focus or show the mouth after treatment. */
  drawing?: TeethDrawing;
  arch: Arch;
  after?: ArchAfter;
  /** Twelve's own callouts, shown or not; absent on a copy frozen before the drawing editor. */
  callouts?: { tooth: number; text: string }[];
  /** Area work by name, "Deep cleaning · Upper right". */
  areas: string[];
};

/** One of Twelve's drawings a practice's library page can stand in for: "treatment:crown", "waits:crack:3". */
export type DrawingKey = string;

export type IfItWaitsStage = {
  caption: string;
  /** Absent on a copy frozen before a practice could swap the drawings. */
  drawing?: DrawingKey;
  /** The practice's page shown in place of Twelve's drawing. */
  practicePicture?: DeckLibraryPage;
};

export type IfItWaitsSlide = SlideBase & {
  type: "if_it_waits";
  path: WaitPath;
  /** The family whose own telling of the path this is ("extraction", which never ends with the tooth lost), so the
   *  drawings match its captions. Null or absent for the path's own. */
  variant?: string | null;
  /** Exactly four, now to worst. */
  stages: IfItWaitsStage[];
  /** The step staff or Ask Twelve made stand out, 1 to 4; the rest fade. */
  focus?: number;
};

export type FamilySlide = SlideBase & {
  type: "family";
  family: FamilyKey;
  art: ArtKey;
  title: string;
  what: string;
  why: string;
  expect: string;
  /** "Tooth #3", "Upper right", "+2 more". */
  sites: string[];
  /** The visit numbers this treatment is part of. */
  visits: number[];
  /** Absent on a copy frozen before a practice could swap the drawings. */
  drawing?: DrawingKey;
  /** The practice's page shown in place of Twelve's drawing. */
  practicePicture?: DeckLibraryPage;
};

type DeckVisit = {
  number: number;
  /** ISO date, only when the visit is booked. */
  date: string | null;
  /** "#3 Buildup + Crown", "Deep cleaning · Upper right", "Exam". */
  items: string[];
  arch: Arch;
};

export type VisitsSlide = SlideBase & {
  type: "visits";
  visits: DeckVisit[];
};

/** One choice on Your options: a treatment plan, or "Wait for now" (key "wait"). */
export type DeckOption = {
  /** "plan_<plan id>" or "wait". */
  key: string;
  title: string;
  /** The treatments' names, "Implant", "Crown". Empty for "Wait for now". */
  treatments: string[];
  visits: number;
  /** Planned chair time, when the plan has any. */
  minutes: number | null;
  /** "Good to know": the library's words, or the staff's. */
  upsides: string[];
  /** "Keep in mind". */
  considerations: string[];
  /** The doctor's pick, marked by staff. */
  recommended: boolean;
  /** The teeth this plan works on, when staff show each option's teeth. Never on "Wait for now". */
  arch?: Arch;
};

/** Up to three plans side by side, when the visits sit in more than one, plus "Wait for now". */
export type OptionsSlide = SlideBase & {
  type: "options";
  options: DeckOption[];
};

type TimelineStep = {
  number: number;
  /** ISO date: the booked day, else the day the plan pencils it in for. */
  date: string | null;
  /** The date is a booked appointment. */
  booked: boolean;
  items: string[];
  minutes: number | null;
  /** What happens before the next visit, "The implant heals into the bone before the new tooth goes on." */
  wait: string | null;
};

/** The visits as steps with the time between them. Shown for work that spans visits with healing or lab time. */
export type TimelineSlide = SlideBase & {
  type: "timeline";
  steps: TimelineStep[];
  /** Total planned chair time. */
  minutes: number | null;
};

/** Care tips per treatment, from the library. */
export type CareSlide = SlideBase & {
  type: "care";
  families: { family: FamilyKey; art: ArtKey; title: string; tips: string[] }[];
};

/** The plan's fees, from its rows: never typed. Only in a deck when staff add it. */
export type CostSlide = SlideBase & {
  type: "cost";
  rows: {
    name: string;
    site: string | null;
    feeCents: number;
    insuranceCents: number;
    patientCents: number;
  }[];
  feeCents: number;
  insuranceCents: number;
  patientCents: number;
  /** Show the insurance estimate and the patient's part; off shows the fee alone. */
  insurance: boolean;
  note: string | null;
};

/** A slide staff wrote: a headline and up to four lines. */
export type TextSlide = SlideBase & {
  type: "text";
  lines: string[];
};

export type QuestionsSlide = SlideBase & {
  type: "questions";
  lead: string;
  steps: { title: string; detail: string }[];
};

export type Slide =
  | CoverSlide
  | WhatWeFoundSlide
  | PictureSlide
  | LibraryPageSlide
  | YourTeethSlide
  | IfItWaitsSlide
  | FamilySlide
  | VisitsSlide
  | OptionsSlide
  | TimelineSlide
  | CareSlide
  | CostSlide
  | TextSlide
  | ImagesSlide
  | QuestionsSlide;
export type SlideType = Slide["type"];

export type Deck = {
  version: 1;
  patientName: string;
  officeName: string;
  presenterName: string;
  slides: Slide[];
  /** A deck in the patient's language: its locale and the words the slides draw around its own (deck_words). */
  locale?: string;
  /** A counted label is a hash of plural forms ({ one, few, many, other }). */
  labels?: Partial<Record<DeckLabelKey, string | Record<string, string>>>;
};

/** Staff's own points for one column of Your options. A field left out keeps Twelve's. */
export type OptionColumnEdit = { key: string; upsides?: string[]; considerations?: string[] };

/** The staff's changes to the slides (TreatmentPresentations::SlideEdits), saved whole on every change. */
export type SlideEdits = {
  hidden?: string[];
  /** Slides staff removed: off the deck until brought back. A text slide is deleted from texts instead. */
  removed?: string[];
  /** Slides hidden by default that staff show: "cost", or "visits" beside a timeline. */
  shown?: string[];
  order?: string[];
  headlines?: { slide: string; text: string }[];
  fields?: {
    options?: {
      columns?: OptionColumnEdit[];
      recommended?: string | null;
      wait?: boolean;
      /** Each plan's column draws its teeth. */
      teeth?: boolean;
    };
    /** The If it waits step that stands out. */
    wait?: { focus?: number };
    cost?: { note?: string | null; insurance?: boolean };
    /** Your teeth's drawing: a quick view or the drawing's own parts, staff's notes, and whether the planned teeth
     *  are named. */
    /** Library pages' frames and the office's words beside them, by page id. */
    pages?: { page: string; frame?: PageFrame; words?: string }[];
    /** Pictures' frames, Pearl's marks hidden, and staff's pointers, by photo id. */
    pictures?: {
      photo: string;
      frame?: PageFrame;
      marks?: boolean;
      notes?: PictureNote[];
      words?: string;
    }[];
    teeth?: TeethDrawing & {
      zoom?: ArchZoom;
      labels?: boolean;
      notes?: { tooth: number; text: string }[];
    };
    /** This presentation's own swaps of Twelve's drawings: a library page in its place, or null for Twelve's drawing
     *  where the library has a page for it. */
    drawings?: { drawing: DrawingKey; page: LibraryPageId | null }[];
  };
  texts?: { key: string; headline: string; lines: string[] }[];
  /** Staff's own picture slides, keyed "images_<n>": one or two pictures from Imaging (photo) or Files (file). */
  images?: ImagesEdit[];
};

export type ImagesItemEdit = (
  | { photo: PhotoId; file?: undefined }
  | { file: PatientFileId; photo?: undefined }
) & {
  caption?: string;
};
export type ImagesEdit = { key: string; headline: string; words?: string; items: ImagesItemEdit[] };

/** What an image URL is made from: a picture's photo id and the orientation its marks were drawn for, or a library
 *  page's id, which has no orientation. The live page has a route for each; a frozen copy serves both by id. */
export type DeckImageRef =
  | { kind: "photo"; photoId: PhotoId; orientationKey: string }
  | { kind: "library_page"; pageId: LibraryPageId }
  | { kind: "file"; fileId: PatientFileId };

/** The id a frozen copy's image route serves an image under: a photo's, a library page's or a patient file's. */
export function deckImageId(image: DeckImageRef): PhotoId | LibraryPageId | PatientFileId {
  switch (image.kind) {
    case "photo":
      return image.photoId;
    case "library_page":
      return image.pageId;
    case "file":
      return image.fileId;
  }
}

/** A deck picture as the image routes want it. */
export function photoRef(picture: Pick<DeckPicture, "photoId" | "orientationKey">): DeckImageRef {
  return { kind: "photo", photoId: picture.photoId, orientationKey: picture.orientationKey };
}

/** An item of staff's own picture slide as the image routes want it. */
export function itemRef(item: ImagesItem): DeckImageRef {
  return item.picture ? photoRef(item.picture) : { kind: "file", fileId: item.file.fileId };
}

/** A video library page's file, which the slide plays over the page's still. */
export type DeckVideoRef = { kind: "library_video"; pageId: LibraryPageId };

/** How a page turns a picture into an image URL, or a video page into the video's. Each page passes its own route
 *  helpers; the deck holds no URLs. */
export type PictureSrc = (image: DeckImageRef | DeckVideoRef) => string;

/** A frozen copy's URL for an image or a video: the copy serves its images by id and its videos by page id. */
export function frozenSrc(
  image: DeckImageRef | DeckVideoRef,
  routes: {
    image: (id: ReturnType<typeof deckImageId>) => string;
    video: (pageId: LibraryPageId) => string;
  },
): string {
  return image.kind === "library_video"
    ? routes.video(image.pageId)
    : routes.image(deckImageId(image));
}

// ---- Staff "Present treatment" page: embedded/treatment_presentations/show ----

type PresentationStatus = "drafting" | "ready" | "failed";

type BoxSourceKind =
  | "procedure"
  | "condition"
  | "history"
  | "exam_note"
  | "recording"
  | "perio_chart"
  | "missing_tooth"
  | "staff_reason"
  | "picture"
  | "pearl";

export type BoxSource = {
  kind: BoxSourceKind;
  /** Chip text: "Exam note", "Exam note (draft)", "Plan: Crown #3", "Pearl: tartar on Bitewings · UR BW",
   *  "Recording, 10:42 in: …", "Perio chart, Jul 8: …", "Reason: a crack · picked by Jen Rivera". */
  label: string;
  /** Tooltip: the cited exam-note sentence or Tooth Watch note. Staff only. */
  detail: string | null;
  /** A box chip for a staff pick only: the pick it stands on (its path and the picked treatments), by which the server
   *  tells when that pick is withdrawn. Never shown. */
  key?: string;
};

export type BoxSentence = {
  text: string;
  /** Staff changed the AI's words; the chips still show what the AI grounded it on. */
  edited: boolean;
  sources: BoxSource[];
  /** The staff reason this sentence cited was withdrawn; its chip is gone and the words may be out of date. */
  reasonChanged?: boolean;
};

/** A planned treatment the "If it waits" panel asks or answers about: the first of the treatments of one kind on the
 *  same teeth. */
type WaitReasonSubject = {
  /** What a decision posts. */
  treatmentId: TreatmentId;
  /** "the crown on #19", "the deep cleaning on the upper right", "the deep cleaning". */
  phrase: string;
};

export type WaitReasonOption = {
  path: WaitPath;
  /** The library's reason label, "An old filling". */
  label: string;
  /** Only on the likely reason: "2009 filling on #19", "Pearl: decay on today's x-ray". */
  clue: string | null;
};

type WaitReasonBasis = "finding" | "ai" | "pick";

export type WaitReasonStrip = {
  path: WaitPath;
  /** finding: the chart proves it. ai: a note or the recording says it. pick: staff chose it. */
  basis: WaitReasonBasis;
  /** "A crack · #19", "Gum disease". */
  title: string;
  subject: WaitReasonSubject;
  /** What it rests on, as chips. A pick's one chip reads "Reason: a crack · picked by Jen Rivera". */
  sources: BoxSource[];
  /** What Change offers: the subject's other reasons, in the library's order. */
  others: WaitReasonOption[];
  pickedByName: string | null;
  /** ISO time of the pick. */
  pickedAt: string | null;
};

export type WaitReasonQuestion = {
  subject: WaitReasonSubject;
  /** "Why the crown on #19?" */
  prompt: string;
  /** The likely reason first, with its clue, then the rest in the library's order. */
  options: WaitReasonOption[];
};

/** A decision the panel lists with its undo, which clears it: a treatment staff decided shows no strip, hidden (Show)
 *  or another reason (Ask again), or one whose pick holds though the strip shows another's (Clear the pick); the words
 *  may still state that pick. */
export type WaitReasonDecided = WaitReasonSubject & {
  decidedByName: string;
  /** ISO time of the decision. */
  decidedAt: string;
} & (
    | { kind: "hidden" | "other" }
    | {
        kind: "pick";
        path: WaitPath;
        /** The reason as a sentence says it: "a crack", "after a root canal". */
        reason: string;
      }
  );

/** The "If it waits" panel on the Present screen. Staff only: never in the deck, never on the patient page. */
export type WaitReason = {
  strip: WaitReasonStrip | null;
  question: WaitReasonQuestion | null;
  decided: WaitReasonDecided[];
};

/** What the panel posts to reason…Path. */
export type WaitReasonDecision =
  | { treatmentId: TreatmentId; decision: "pick"; waitPath: WaitPath }
  | { treatmentId: TreatmentId; decision: "other" | "hidden" | "clear" };

export type Presentation = {
  id: TreatmentPresentationId;
  /** The treatments the deck names, "Crown, Deep cleaning", else "Treatment plan". */
  title: string;
  status: PresentationStatus;
  /** Bumped on every deck change. "This device" records the revision it is showing. */
  revision: number;
  treatmentVisitIds: TreatmentVisitId[];
  patientName: string;
  /** The patient allows texts. */
  textConsent: boolean;
  presenterName: string;
  picturePhotoIds: PhotoId[];
  /** The library pages on the deck, in order: the ones always shown for its treatments, then the AI's or staff's. */
  libraryPageIds: LibraryPageId[];
  /** The account's library has a page this location shows, so the From our practice section is on. */
  libraryAvailable: boolean;
  /** An account admin, who may make a picture the practice's own for one of Twelve's drawings (Always). */
  canSetDrawings: boolean;
  box: BoxSentence[];
  /** The "If it waits" panel: the strip's reason, the question, and what staff decided shows no strip. */
  waitReason: WaitReason;
  /** A re-draft of the box for the staff's reasons is running; the page polls and the box is read-only until it lands. */
  redrafting: boolean;
  /** The last re-draft failed or died, and staff have not edited the box since. */
  redraftFailed: boolean;
  /** The presenter's own notes for the Present tab. Never on a slide or the patient's copy. */
  talkingPoints: string | null;
  /** Started without Twelve's AI (New presentation's Start without AI): the default slides, never drafted. */
  draftedByRules: boolean;
  /** The newest sealed version holds the deck exactly as it stands: nothing new to seal. */
  draftSealed: boolean;
  slideEdits: SlideEdits;
  /** Ask Twelve's change, while it still stands on the slides on screen; null when there is none. */
  aiProposal: AiProposal | null;
  /** The working deck, hidden slides included (marked hidden). Always English: the composer writes it, and staff's
   *  slide fields edit it. */
  deck: Deck;
  /** The locale the patient sees the slides in ("en", "es"), from the chart when the presentation started. */
  language: string;
  /** The languages the office offers, in its order; the one in use is kept even if the office stopped offering it. */
  languages: PresentationLanguage[];
  /** The deck as the patient reads it in `language`; null in English. */
  localizedDeck: Deck | null;
  /** Each line a patient reads, in English and in `language`; null in English. */
  translation: Translation | null;
};

export type PresentationLanguage = {
  code: string;
  /** In the language itself: "Español". */
  name: string;
  englishName: string;
  /** The patient's chart says this language. */
  chart: boolean;
};

/** One line a patient reads on a slide: its English and its words in the presentation's language. */
export type TranslationLine = {
  /** The slide's key. */
  slide: string;
  key: string;
  english: string;
  /** null while Twelve's translation is on its way, or when it couldn't translate the line. */
  text: string | null;
  /** Staff's own words, which a fresh translation keeps. */
  own: boolean;
};

export type Translation = {
  /** untranslated: a line the patient sees has no translation, and nothing is translating it now. */
  state: "translating" | "untranslated" | "ready";
  lines: TranslationLine[];
  /** Slide types that stay as the practice made them: a booklet page or video. */
  untranslatedSlides: string[];
};

/** A change to the slides Ask Twelve proposed: the whole set of slide edits it would save, the slides it changes, and
 *  the deck as it would read. Nothing changes until staff use it. */
export type AiProposal = {
  revision: number;
  ask: string;
  /** Redo with AI's one slide, or null for the whole deck. */
  slide: string | null;
  edits: SlideEdits;
  changes: { slide: string; title: string; summary: string }[];
  deck: Deck;
  /** Said beside the changes: they are only the closest Twelve can do, or a second look found part of the ask not
   *  done. Absent on a plain proposal and on one made before notes. */
  note?: string | null;
  /** The deck's library pages as the proposal has them, or null when it leaves them as they are. */
  libraryPageIds?: LibraryPageId[] | null;
  askedBy: string;
  askedAt: string;
};

/** Twelve asks which reading staff meant before changing anything. */
export type AskQuestion = { question: string; choices: string[] };

// ---- Sealed versions: what Present, Text patient, the QR code and Mark presented leave behind ----

/** How a version came to be sealed: shown on this device, texted, shown as a QR code, or marked presented. */
export type SealedVia = "screen" | "text" | "qr" | "marked";
export type ShownVia = "screen" | "marked";

/** What the patient said to a version. Absent: shown, with no decision recorded yet. */
export type Decision = "accepted" | "thinking" | "declined";

export type PlanLine = {
  treatmentId: TreatmentId;
  code: string;
  name: string;
  /** "#3 MOD", "#14", or null for work with no tooth. */
  site: string | null;
  feeCents: number;
  insuranceCents: number;
  patientCents: number;
};

/** The treatment and fees as they stood when the version was sealed; no lines for a copy sealed before fees were
 *  kept. */
export type PlanRecord = {
  lines: PlanLine[];
  feeCents: number;
  insuranceCents: number;
  patientCents: number;
};

export type VersionSummary = {
  id: TreatmentPresentationSnapshotId;
  version: number;
  sealedAt: string;
  sealedVia: SealedVia;
  shownAt: string | null;
  /** How it was shown, when it was: on this device, or marked presented by hand. Its own field, since a copy texted
   *  first and shown later is sealed by the text. */
  shownVia: ShownVia | null;
  textedAt: string | null;
  qrShownAt: string | null;
  /** The first open of the patient link past the identity check, or by a scanned code. */
  openedAt: string | null;
  presenterName: string;
  /** Who presented, picked when it started. */
  presentedByName: string;
  /** Whoever was signed in when it was sealed, when that was someone else. */
  sealedByName: string | null;
  decision: Decision | null;
  decidedAt: string | null;
  decidedByName: string | null;
  /** ISO date, for a patient still thinking. */
  followUpOn: string | null;
  /** The language the patient saw it in, "Spanish"; null in English. */
  languageName: string | null;
};

/** The version the Records tab opens: the summary with its deck and the plan's fees as sealed. */
export type SelectedVersion = VersionSummary & {
  deck: Deck;
  /** The same slides in the English they were translated from; null for a version shown in English. */
  englishDeck: Deck | null;
  plan: PlanRecord;
  markups: MarkupRecord[];
  /** Where the version was found in a same-day visit recording; null when none is attached, absent without Record
   *  ambient visits (DECISIONS.md 54). */
  recording?: PresentationRecording | null;
};

/** The stretch of a visit recording where a version's plan was talked through. Presented: walked through with the
 *  slides on screen, and coached. Discussed: the same treatment came up, the slides were shown outside the recording. */
export type PresentationRecording = {
  id: string;
  label: "presented" | "discussed";
  startSeconds: number;
  endSeconds: number;
  /** The plan lines heard in the stretch, as the version's plan names them. */
  matchedItems: string[];
  recordedAt: string;
  /** The visit's whole recording, played from startSeconds; null once its audio is gone. */
  audioUrl: string | null;
  /** The stretch's turns only, timed into the whole recording. */
  transcript: { user: string; content: string; startTime: number; endTime: number }[];
  canDetach: boolean;
};

/** A slide's strokes, keyed by slide in a list: slide keys are data, which the prop transformer must not camelize. */
export type SlideStrokes = { slide: string; strokes: Stroke[] };

/** What staff drew on a version at one show, locked once the show ended (DECISIONS.md 53). */
export type MarkupRecord = {
  id: string;
  drawnByName: string;
  drawnAt: string;
  slides: SlideStrokes[];
};

/** A case as the chart's Cases mode lists it. */
export type CaseSummary = {
  id: TreatmentPresentationId;
  title: string;
  status: PresentationStatus;
  createdAt: string;
  presenterName: string;
  treatmentVisitIds: TreatmentVisitId[];
  draftSealed: boolean;
  /** Off the list unless Show archived is on; its versions stay in Files & forms. */
  archived: boolean;
  /** A link was texted: archived rather than deleted, even with no version sealed. */
  texted: boolean;
  newestVersion: VersionSummary | null;
};

export type PictureOption = {
  photoId: PhotoId;
  kind: "xray" | "photo";
  label: string;
  takenOn: string;
  today: boolean;
  hasPearl: boolean;
  /** The picture's current orientation ("3-90-10"), so its thumbnail URL changes when it is rotated. */
  orientationKey: string;
};

/** One of the patient's image files the picture slide picker offers. */
export type FileOption = {
  fileId: PatientFileId;
  label: string;
  /** ISO date the file was added. */
  addedOn: string;
};

/** One of the practice's library pages the Change library pages picker offers. */
export type LibraryPageOption = {
  pageId: LibraryPageId;
  title: string;
  /** A video page; its thumbnail is the still. */
  video: boolean;
  /** The staff's "When is this helpful?" note. */
  note: string | null;
  /** Tagged to one of the plan's treatments; those come first. */
  matched: boolean;
};

export type PresentPageProps = {
  locationId: LocationId;
  patientId: PatientId;
  /** The office's IANA zone: the Records tab prints every time in it. */
  timezone: string;
  presentation: Presentation;
  /** Every sealed version, newest first. */
  versions: VersionSummary[];
  /** An optional prop: only sent after router.reload({ only: ["selected_version"] }), with ?version=N for one. */
  selectedVersion?: SelectedVersion | null;
  /** An optional prop: only sent after router.reload({ only: ["picture_options"] }). */
  pictureOptions?: PictureOption[];
  /** An optional prop: only sent after router.reload({ only: ["library_options"] }). */
  libraryOptions?: LibraryPageOption[];
  /** An optional prop: only sent after router.reload({ only: ["file_options"] }). */
  fileOptions?: FileOption[];
  /** Who may be picked as presenting, providers first. */
  presenterOptions: PresenterOption[];
  /** Every link texted for this presentation, newest first. */
  texts: TextSummary[];
  /** Today's visit, which Start can record; null without Record ambient visits or a visit under way. */
  visitRecording: VisitRecording | null;
};

/** The visit recording Start's Record this conversation begins, or keeps going when the visit has one already
 *  (DECISIONS.md 55). */
export type VisitRecording = {
  treatmentVisitId: string;
  /** A consented recording of the visit exists: ticking adds to it, with no fresh consent. */
  resumable: boolean;
};

/** A person who may present: the location's staff and providers, and whoever is signed in. */
export type PresenterOption = {
  id: AccountUserId;
  name: string;
  provider: boolean;
};

/** A texted link: a text seals no version, so it is listed on its own. */
export type TextSummary = {
  id: TreatmentPresentationSnapshotId;
  textedAt: string;
  sentByName: string;
  openedAt: string | null;
  /** When the link stops opening; null for a link texted before links expired. */
  linkExpiresAt: string | null;
  /** The version these slides became, when they were presented too. */
  version: number | null;
  /** Sent with Include drawings. */
  withDrawings: boolean;
};

/** Where the tablet that scanned the start code and the screen keep to the same slide. */
export type LiveSlide = {
  key: string | null;
  seq: number;
  /** The read only: the drawing on the version now, `<layer id>.<revision>`, null before its first line. */
  drawn?: string | null;
  /** The read only, when `drawn` is not the one asked about: its strokes. */
  drawings?: SlideStrokes[];
};

/** What POST qr…Path answers: the code another device scans, good for a few minutes. */
export type QrResponse = {
  snapshotId: TreatmentPresentationSnapshotId;
  version: number;
  url: string;
  qrSvg: string;
  expiresInMinutes: number;
};

/** What POST textPreview…Path answers. */
export type TextPreviewResponse = {
  body: string;
  snapshotId: TreatmentPresentationSnapshotId;
  /** The newest drawing on the copy, which Include drawings sends; null when it was never presented or drawn on. */
  drawings: { drawnAt: string; drawnByName: string } | null;
};

/** What POST present…Path answers: the copy This device opens in the presenter window. */
export type PresentResponse = {
  snapshotId: TreatmentPresentationSnapshotId;
  version: number;
};

// ---- Staff snapshot page: embedded/treatment_presentation_snapshots/show ----

/** Why a frozen copy showed its "If it waits" strip, every other pick its words may state, and what staff hid. Staff
 *  only. */
export type SnapshotWaitReason = {
  strip: {
    path: WaitPath;
    basis: WaitReasonBasis;
    title: string;
    subjectPhrase: string;
    sources: BoxSource[];
    pickedByName: string | null;
    pickedAt: string | null;
  } | null;
  /** Every other pick that held, which its words may state: "the crown on #13", "after a root canal". */
  picks: {
    phrase: string;
    path: WaitPath;
    reason: string;
    pickedByName: string;
    pickedAt: string;
  }[];
  hidden: { phrase: string; decidedByName: string; decidedAt: string }[];
};

export type SnapshotPageProps = {
  locationId: LocationId;
  patientId: PatientId;
  /** The office's IANA zone: Shown and Texted print in it, like the Files & forms row title. */
  timezone: string;
  snapshot: {
    id: TreatmentPresentationSnapshotId;
    deck: Deck;
    presenterName: string;
    version: number;
    shownAt: string | null;
    textedAt: string | null;
    qrShownAt: string | null;
    decision: Decision | null;
    ifItWaitsReason: SnapshotWaitReason | null;
    /** What was drawn at each show, oldest first. */
    markups: MarkupRecord[];
  };
};

// ---- Presenter window: embedded/treatment_presentation_snapshots/window ----

export type PresenterWindowPageProps = {
  locationId: LocationId;
  patientId: PatientId;
  /** The case the copy is a version of: the channel the case page's stage drives this window on. */
  presentationId: TreatmentPresentationId;
  snapshotId: TreatmentPresentationSnapshotId;
  deck: Deck;
  /** The show's drawing so far, so a reloaded window keeps it; null before anything is drawn. */
  markup: SlideStrokes[] | null;
  /** The window's controls in the language the copy was frozen in: it is the patient's screen. */
  labels: {
    slides: string;
    back: string;
    next: string;
    fullScreen: string;
    exitFullScreen: string;
  };
  drawLabels: MarkupLabels;
};

// ---- Patient page: public/treatment_presentation/show ----

export type PublicPageProps = {
  locationId: LocationId;
  snapshotId: TreatmentPresentationSnapshotId;
  deck: Deck;
  /** A texted link sent with Include drawings: the lines as sent. null otherwise, and on the office tablet. */
  drawings: SlideStrokes[] | null;
  /** Opened by the start code on the office's tablet: it keeps to the same slide as the screen. */
  live: boolean;
};

// ---- Files & forms, Agreements tab ----

export type PresentationRow = {
  id: TreatmentPresentationSnapshotId;
  kind: "presentation";
  /** The latest of shown and texted, "October 08, 2026", in the office's zone. */
  title: string;
  texted: boolean;
};

export type SelectedPresentation = {
  id: TreatmentPresentationSnapshotId;
  kind: "presentation";
  title: string;
  shownAt: string | null;
  textedAt: string | null;
  presenterName: string;
  version: number;
  decision: Decision | null;
  /** The case it belongs to, where Records shows it in full. */
  presentationId: TreatmentPresentationId;
  /** The office's IANA zone: Shown and Texted print in it, like the row title. */
  timezone: string;
};
